import "./Navigation.css";
import { ReactComponent as MenuIcon } from "../assets/icons/plus.svg";
import { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import { ReactComponent as ArrowIconDiagL } from "../assets/icons/arrow-large.svg";
import { ReactComponent as SnowIcon } from "../assets/icons/snow.svg";
import { ReactComponent as SunIcon } from "../assets/icons/sun.svg";
import { ReactComponent as CloudIcon } from "../assets/icons/cloud.svg";
import { ReactComponent as RainIcon } from "../assets/icons/rain.svg";
import { ReactComponent as ThunderIcon } from "../assets/icons/thunder.svg";
import { motion, AnimatePresence } from "motion/react";
import { FadeIn, StaggerIn, StaggerItem } from "./FadeIn";

// slide in menu https://stackoverflow.com/questions/48143381/css-expand-contract-animation-to-show-hide-content
// slide in with classes: https://stackoverflow.com/questions/37677059/slideup-and-slidedown-animation-using-react-js?rq=3
// slide in current implementation: https://stackoverflow.com/questions/42588171/how-to-perform-jquery-slidetoggle-equivalent-in-reactjs
// animation delay: https://stackoverflow.com/questions/8294400/css-animations-with-delay-for-each-child-element
// showing text on hover: https://stackoverflow.com/questions/64709508/display-text-when-hovering-over-an-icon-using-react-icon-library

const containerVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.08,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      when: "afterChildren",
      staggerChildren: 0.08,
      staggerDirection: -1,
      ease: "easeIn",
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 }, // Start off-screen + transparent
  visible: {
    opacity: 1, // Fade in
    y: 0, // Slide up
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1], // Smooth spring-like easing
    },
  },
};

function NavigationCollapsible() {
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  return (
    <nav>
      <div className="navigation">
        <Link to="/" className="home-link">
          <p>melanie imfeld</p>
        </Link>
        <MenuIcon
          className={`menuIcon1 ${isOpen ? "active" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="menu"
          >
            {["work", "media", "about"].map((item, index) => (
              <MenuItem
                title={item}
                to={item === "work" ? "/" : `/${item}`}
                variants={itemVariants}
                key={index}
                onClick={handleMenuItemClick}
              />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </nav>
  );
}

function Menu({ style, display }) {
  return (
    <ul className={`menu ${display}`} style={style}>
      <MenuItem title="work" to="/" />
      <MenuItem title="media" to="/media" />
      <MenuItem title="about" to="/about" />
    </ul>
  );
}

function MenuItem({ title, to, variants, onClick }) {
  return (
    <motion.li className="menuitem" variants={variants}>
      {" "}
      <Link to={to} className="menuitem-link" onClick={onClick}>
        <p> {title}</p>
      </Link>
    </motion.li>
  );
}

// function MenuItem({ title, to, variants }) {
//   return (
//     <li className="menuitem">
//       {" "}
//       <Link to={to} className="menuitem-link">
//         <p> {title}</p>
//         <ArrowIconDiag className="arrowUpAnimation"></ArrowIconDiag>
//       </Link>
//     </li>
//   );
// }

function NavigationDesktop() {
  return (
    <nav className="navigation">
      <Link to="/" className="home-link">
        <p>melanie imfeld</p>
      </Link>
      <Menu display="horizontal" />
    </nav>
  );
}

function Navigation() {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return <>{isMobile ? <NavigationCollapsible /> : <NavigationDesktop />}</>;
  //return <CollapsibleNavigation></CollapsibleNavigation>;
}

const headerText = (
  <>
    I'm passionate about data storytelling, designing interfaces for complex
    developer tools, and prototyping with code - currently bringing these
    interests together at{" "}
    <HeaderLink url="www.uber.com" linkText="Uber"></HeaderLink>.{" "}
  </>
);

export function Header() {
  return (
    <StaggerIn className="header">
      <StaggerItem>
        <Navigation />
      </StaggerItem>
      <StaggerItem as="h1">{headerText}</StaggerItem>
    </StaggerIn>
  );
}

function HeaderLink({ url, linkText }) {
  return (
    <a
      href={`https://${url}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0px",
      }}
      target="_blank"
      rel="noreferrer"
    >
      {linkText}
      <ArrowIconDiagL className="headerArrow arrowUpAnimation"></ArrowIconDiagL>
    </a>
  );
}

export function Footer() {
  return (
    <FadeIn delay={0.1} className="footer">
      <div className="navigation">
        <p>
          <a href="mailto:indemfeld@gmail.com">say hello</a>
        </p>
        <WeatherWidget />
      </div>
    </FadeIn>
  );
}

const WEATHER_CACHE_KEY = "weather-cache";
const WEATHER_CACHE_TTL_MS = 30 * 60 * 1000;

function WeatherWidget() {
  const [temperature, setTemperature] = useState("45F");
  const [weather, setWeather] = useState("sun");
  const coordinates = "(37.7,- 122.4)";
  const lon = "-122.3992";
  const lat = "37.75708";

  const API_KEY = "d457039d92fcf2865b4d4e38ff44d6c8";
  const API_URL = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=imperial`;

  const weatherIcons = {
    clear: <SunIcon />,
    rain: <RainIcon />,
    clouds: <CloudIcon />,
    snow: <SnowIcon />,
    thunderstorm: <ThunderIcon />,
    drizzle: <RainIcon />,
    mist: <CloudIcon />,
    default: <SunIcon />,
  };

  useEffect(() => {
    const cached = localStorage.getItem(WEATHER_CACHE_KEY);
    if (cached) {
      try {
        const { temperature: cachedTemp, weather: cachedWeather, timestamp } =
          JSON.parse(cached);
        if (Date.now() - timestamp < WEATHER_CACHE_TTL_MS) {
          setTemperature(cachedTemp);
          setWeather(cachedWeather);
          return;
        }
      } catch {
        localStorage.removeItem(WEATHER_CACHE_KEY);
      }
    }

    const fetchWeatherData = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();
        const temp = `${Math.round(data.main.temp)}F`;
        const condition = data.weather[0].main.toLowerCase();
        setTemperature(temp);
        setWeather(condition);
        localStorage.setItem(
          WEATHER_CACHE_KEY,
          JSON.stringify({
            temperature: temp,
            weather: condition,
            timestamp: Date.now(),
          }),
        );
      } catch (error) {
        console.error("Error fetching weather data:", error);
      }
    };

    fetchWeatherData();
  }, [API_URL]);

  return (
    <div className="weatherwidget">
      <p>{coordinates}</p>
      <p>{temperature}</p>
      {weatherIcons[weather] || weatherIcons.default}
    </div>
  );
}
